import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUsers, FaStar } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

// /api/roles returns every role, including "Водитель" (453 people) and
// "Retired Member VTC" (310). Rendering all 889 cards made this page 69 560px
// tall — 77 screens — so large rosters start collapsed.
const PREVIEW_SIZE = 12;
const HIDDEN_MEMBER_IDS = new Set([169]);

const removeHiddenMembers = (roles) => roles
  .map(role => ({
    ...role,
    users: (role.users || []).filter(user => !HIDDEN_MEMBER_IDS.has(Number(user.id))),
  }))
  .filter(role => role.users.length > 0);

const Members = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRoles, setExpandedRoles] = useState({});
  const { t } = useTranslation();
  const membersRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (membersRef.current) observer.observe(membersRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const cacheKey = 'rolesData';
    const cacheTimestampKey = 'rolesDataTimestamp';

    const fetchRoles = async () => {
      try {
        const cachedData = localStorage.getItem(cacheKey);
        const cachedTimestamp = localStorage.getItem(cacheTimestampKey);

        const isCacheValid = cachedTimestamp && (new Date().getTime() - new Date(parseInt(cachedTimestamp)).getTime()) < 1 * 3600 * 1000;

        if (cachedData && isCacheValid) {
          setRoles(removeHiddenMembers(JSON.parse(cachedData)));
        } else {
          const response = await fetch('https://hub.mileageriot.com/api/roles');
          const data = await response.json();
          const rolesWithUsers = removeHiddenMembers(data);
          setRoles(rolesWithUsers);
          localStorage.setItem(cacheKey, JSON.stringify(rolesWithUsers));
          localStorage.setItem(cacheTimestampKey, new Date().getTime().toString());
        }
      } catch (error) {
        console.error('Error fetching roles data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const toggleRole = (roleId) => {
    setExpandedRoles(prev => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  if (loading) {
    return (
      <section className="members-modern page-members" ref={membersRef}>
        <div className="container">
          <div className="text-center">
            <div className="loading-spinner">
              <div className="spinner-modern"></div>
            </div>
            <p className="mt-3 text-white-50">{t('members.loading') || 'Загружаем команду...'}</p>
          </div>
        </div>
      </section>
    );
  }

  const totalMembers = roles.reduce((sum, role) => sum + role.users.length, 0);

  return (
    <>
    <Helmet>
      <title>Команда — Mileage Riot ВТК | ETS2 & ATS</title>
      <meta name="description" content="Команда Mileage Riot — управление, отделы и участники виртуальной транспортной компании для ETS2 и ATS." />
      <link rel="canonical" href="https://mileageriot.com/staff" />
      <meta property="og:title" content="Команда — Mileage Riot" />
      <meta property="og:url" content="https://mileageriot.com/staff" />
      <meta property="og:locale" content="ru_RU" />
    </Helmet>
    <section className="members-modern page-members" ref={membersRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaUsers className="me-2" />
            <span>{t('members.badge') || 'Команда'}</span>
          </div>
          <h1 className="section-title text-glow text-white">
            {t('members.title') || 'Знакомство с командой'}
          </h1>
          <div className="section-decoration mx-auto"></div>
          <p className="text-white-50 mt-3 section-subtitle">
            {t('members.subtitle') || 'Наши преданные профессионалы, работающие над улучшением нашего сообщества'}
          </p>
          {totalMembers > 0 && (
            <p className="members-total">
              {roles.length} {t('members.rolesLabel', 'ролей')} · {totalMembers} {t('members.peopleLabel', 'участников')}
            </p>
          )}
        </div>

        {/* Roles Sections */}
        <div className="members-content">
          {roles.map((role, index) => {
            const isExpanded = expandedRoles[role.id];
            // Only worth collapsing when a decent chunk is hidden — a 13-person
            // roster offering to reveal 1 more is just a pointless click.
            const isLong = role.users.length > PREVIEW_SIZE + 4;
            const visibleUsers = isLong && !isExpanded ? role.users.slice(0, PREVIEW_SIZE) : role.users;
            const hiddenCount = role.users.length - visibleUsers.length;

            return (
              <div key={role.id} className="role-section fade-in-up" style={{ animationDelay: `${Math.min(index, 6) * 0.1}s` }}>
                <div className="role-header">
                  <div className="role-title-container">
                    {role.icon && (
                      <img loading="lazy" decoding="async"
                        src={`https://hub.mileageriot.com/storage/roles/${role.icon}`}
                        width="44"
                        height="44"
                        alt=""
                        className="role-icon"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    <h2 className="role-title text-white">{role.name}</h2>
                    <span className="role-count">{role.users.length}</span>
                  </div>
                  <div className="role-divider"></div>
                </div>

                {role.another_description && (
                  <div className="role-description">
                    <p className="text-white-50">{role.another_description}</p>
                  </div>
                )}

                <div className="members-grid">
                  {visibleUsers.map((user) => (
                    <div key={user.id} className="member-card glass-container">
                      <div className="member-avatar-container">
                        <img loading="lazy" decoding="async"
                          className="member-avatar"
                          src={user.avatar}
                          alt=""
                          width="84"
                          height="84"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user.name) + '&color=00cfe8&background=1d1e26&size=128';
                          }}
                        />
                        <div className="member-role-badge">
                          <FaStar className="member-badge-icon" />
                        </div>
                      </div>
                      {/* The role is already the section heading, so repeating it
                          under every name was pure noise. */}
                      <h3 className="member-name text-white">{user.name}</h3>
                    </div>
                  ))}
                </div>

                {isLong && (
                  <div className="role-more">
                    <button
                      type="button"
                      className="btn btn-hero-secondary"
                      onClick={() => toggleRole(role.id)}
                      aria-expanded={!!isExpanded}
                    >
                      {isExpanded
                        ? (t('members.showLess') || 'Свернуть')
                        : `${t('members.showAll') || 'Показать всех'} (${hiddenCount})`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Background decoration */}
      <div className="members-bg-decoration"></div>
    </section>
    </>
  );
};

export default Members;
